/**
 * Food results for the marketplace.
 *
 * A dish is the `menu` blueprint made concrete for a shopper: one size choice,
 * a set of paid extras, and the ingredients that are already in it — each of
 * which can be taken out. "No onions" is a first-class action, not a note the
 * kitchen might miss.
 */

export interface DishSize {
  id: string
  label: string
  /** What it costs at this size, so the price updates as you choose. */
  price: number
  note?: string
}

export interface DishExtra {
  id: string
  label: string
  price: number
  group: string
}

export interface DishIncluded {
  id: string
  label: string
  /** Some things can't come out — a burger without the bun isn't a burger. */
  removable: boolean
}

export interface Dish {
  sizes: DishSize[]
  extras: DishExtra[]
  included: DishIncluded[]
  prepTime: string
  /** Shown so nobody has to ask before ordering. */
  allergens: string[]
  spiceLevel?: string
  calories?: string
}

const BURGER =
  'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&q=80&w=800'
const BURGER_2 =
  'https://images.unsplash.com/photo-1550547660-d9450f859349?auto=format&fit=crop&q=80&w=800'
const SMASH =
  'https://images.unsplash.com/photo-1586190848861-99aa4a171e90?auto=format&fit=crop&q=80&w=800'
const CHICKEN =
  'https://images.unsplash.com/photo-1606755962773-d324e0a13086?auto=format&fit=crop&q=80&w=800'
const TRUFFLE =
  'https://images.unsplash.com/photo-1615297928064-24977384d0da?auto=format&fit=crop&q=80&w=800'
const VEG =
  'https://images.unsplash.com/photo-1520072959219-c595dc870360?auto=format&fit=crop&q=80&w=800'
const WAGYU =
  'https://images.unsplash.com/photo-1553979459-d2229ba7433a?auto=format&fit=crop&q=80&w=800'

/** Sauces and cheeses recur, so they are written once. */
const SAUCE_EXTRAS: DishExtra[] = [
  {
    id: 'x-truffle',
    label: 'Truffle mayo',
    price: 8,
    group: 'Sauces',
  },
  {
    id: 'x-chipotle',
    label: 'Chipotle aioli',
    price: 6,
    group: 'Sauces',
  },
  {
    id: 'x-bbq',
    label: 'Smoked BBQ',
    price: 5,
    group: 'Sauces',
  },
]

const SIDE_EXTRAS: DishExtra[] = [
  {
    id: 'x-fries',
    label: 'Skin-on fries',
    price: 14,
    group: 'Make it a meal',
  },
  {
    id: 'x-trufflefries',
    label: 'Truffle parmesan fries',
    price: 22,
    group: 'Make it a meal',
  },
  {
    id: 'x-slaw',
    label: 'House slaw',
    price: 10,
    group: 'Make it a meal',
  },
  {
    id: 'x-drink',
    label: 'Soft drink',
    price: 8,
    group: 'Make it a meal',
  },
]

export interface FoodListing {
  id: string
  title: string
  price: string
  location: string
  images: string[]
  specs: { label: string; value: string }[]
  matchScore: number
  sellerType: 'Dealer' | 'Individual' | 'Verified Business'
  dish: Dish
}

export const foodListings: FoodListing[] = [
  {
    id: 'f1',
    title: 'Double Smash Burger',
    price: 'AED 42',
    location: 'Pitfire Kitchen · Jumeirah',
    images: [SMASH, BURGER],
    specs: [
      {
        label: 'Portion',
        value: 'Double patty',
      },
      {
        label: 'Ready in',
        value: '15–20 min',
      },
      {
        label: 'Spice',
        value: 'Mild',
      },
      {
        label: 'Rating',
        value: '4.8 · 2.1k',
      },
    ],
    matchScore: 97,
    sellerType: 'Verified Business',
    dish: {
      prepTime: '15–20 min',
      allergens: ['Gluten', 'Dairy', 'Egg', 'Sesame'],
      spiceLevel: 'Mild',
      calories: '780 kcal',
      sizes: [
        {
          id: 'single',
          label: 'Single patty',
          price: 32,
        },
        {
          id: 'double',
          label: 'Double patty',
          price: 42,
          note: 'Most ordered',
        },
        {
          id: 'triple',
          label: 'Triple patty',
          price: 54,
        },
      ],
      included: [
        {
          id: 'i-bun',
          label: 'Toasted brioche bun',
          removable: false,
        },
        {
          id: 'i-beef',
          label: 'Smashed beef patty',
          removable: false,
        },
        {
          id: 'i-cheese',
          label: 'American cheese',
          removable: true,
        },
        {
          id: 'i-onion',
          label: 'Grilled onions',
          removable: true,
        },
        {
          id: 'i-pickle',
          label: 'Pickles',
          removable: true,
        },
        {
          id: 'i-lettuce',
          label: 'Lettuce',
          removable: true,
        },
        {
          id: 'i-sauce',
          label: 'House burger sauce',
          removable: true,
        },
      ],
      extras: [
        {
          id: 'x-patty',
          label: 'Extra patty',
          price: 14,
          group: 'Add to your burger',
        },
        {
          id: 'x-cheese',
          label: 'Extra cheese slice',
          price: 5,
          group: 'Add to your burger',
        },
        {
          id: 'x-bacon',
          label: 'Turkey bacon',
          price: 9,
          group: 'Add to your burger',
        },
        {
          id: 'x-jalapeno',
          label: 'Jalapeños',
          price: 4,
          group: 'Add to your burger',
        },
        ...SAUCE_EXTRAS,
        ...SIDE_EXTRAS,
      ],
    },
  },
  {
    id: 'f2',
    title: 'Crispy Chicken Burger',
    price: 'AED 38',
    location: 'Pitfire Kitchen · Jumeirah',
    images: [CHICKEN, BURGER_2],
    specs: [
      {
        label: 'Portion',
        value: 'Regular',
      },
      {
        label: 'Ready in',
        value: '15 min',
      },
      {
        label: 'Spice',
        value: 'Medium',
      },
      {
        label: 'Rating',
        value: '4.7 · 980',
      },
    ],
    matchScore: 94,
    sellerType: 'Verified Business',
    dish: {
      prepTime: '15 min',
      allergens: ['Gluten', 'Dairy', 'Mustard'],
      spiceLevel: 'Medium',
      calories: '690 kcal',
      sizes: [
        {
          id: 'reg',
          label: 'Regular',
          price: 38,
        },
        {
          id: 'large',
          label: 'Large fillet',
          price: 46,
        },
      ],
      included: [
        {
          id: 'i-bun2',
          label: 'Potato bun',
          removable: false,
        },
        {
          id: 'i-chicken',
          label: 'Buttermilk chicken fillet',
          removable: false,
        },
        {
          id: 'i-slaw',
          label: 'Crunchy slaw',
          removable: true,
        },
        {
          id: 'i-onion2',
          label: 'Red onion',
          removable: true,
        },
        {
          id: 'i-hot',
          label: 'Hot honey glaze',
          removable: true,
        },
        {
          id: 'i-pickle2',
          label: 'Pickles',
          removable: true,
        },
      ],
      extras: [
        {
          id: 'x-fillet',
          label: 'Extra fillet',
          price: 16,
          group: 'Add to your burger',
        },
        {
          id: 'x-cheese2',
          label: 'Cheddar slice',
          price: 5,
          group: 'Add to your burger',
        },
        ...SAUCE_EXTRAS,
        ...SIDE_EXTRAS,
      ],
    },
  },
  {
    id: 'f3',
    title: 'Wagyu Truffle Burger',
    price: 'AED 78',
    location: 'Ember House · DIFC',
    images: [WAGYU, TRUFFLE],
    specs: [
      {
        label: 'Portion',
        value: '220g wagyu',
      },
      {
        label: 'Ready in',
        value: '25 min',
      },
      {
        label: 'Spice',
        value: 'None',
      },
      {
        label: 'Rating',
        value: '4.9 · 640',
      },
    ],
    matchScore: 91,
    sellerType: 'Verified Business',
    dish: {
      prepTime: '25 min',
      allergens: ['Gluten', 'Dairy', 'Egg'],
      calories: '920 kcal',
      sizes: [
        {
          id: 'w180',
          label: '180g wagyu',
          price: 66,
        },
        {
          id: 'w220',
          label: '220g wagyu',
          price: 78,
          note: "Chef's pick",
        },
      ],
      included: [
        {
          id: 'i-bun3',
          label: 'Milk bun',
          removable: false,
        },
        {
          id: 'i-wagyu',
          label: 'Wagyu patty',
          removable: false,
        },
        {
          id: 'i-gruyere',
          label: 'Aged gruyère',
          removable: true,
        },
        {
          id: 'i-onionjam',
          label: 'Caramelised onion jam',
          removable: true,
        },
        {
          id: 'i-rocket',
          label: 'Wild rocket',
          removable: true,
        },
        {
          id: 'i-truffleaioli',
          label: 'Truffle aioli',
          removable: true,
        },
      ],
      extras: [
        {
          id: 'x-foie',
          label: 'Seared foie gras',
          price: 45,
          group: 'Add to your burger',
        },
        {
          id: 'x-egg',
          label: 'Fried egg',
          price: 8,
          group: 'Add to your burger',
        },
        ...SIDE_EXTRAS,
      ],
    },
  },
  {
    id: 'f4',
    title: 'Halloumi & Avocado Burger',
    price: 'AED 36',
    location: 'Green Fork · Al Barsha',
    images: [VEG, BURGER],
    specs: [
      {
        label: 'Portion',
        value: 'Regular',
      },
      {
        label: 'Ready in',
        value: '12 min',
      },
      {
        label: 'Diet',
        value: 'Vegetarian',
      },
      {
        label: 'Rating',
        value: '4.6 · 410',
      },
    ],
    matchScore: 88,
    sellerType: 'Verified Business',
    dish: {
      prepTime: '12 min',
      allergens: ['Gluten', 'Dairy'],
      calories: '540 kcal',
      sizes: [
        {
          id: 'reg4',
          label: 'Regular',
          price: 36,
        },
        {
          id: 'dbl4',
          label: 'Double halloumi',
          price: 46,
        },
      ],
      included: [
        {
          id: 'i-bun4',
          label: 'Sourdough bun',
          removable: false,
        },
        {
          id: 'i-halloumi',
          label: 'Grilled halloumi',
          removable: false,
        },
        {
          id: 'i-avo',
          label: 'Smashed avocado',
          removable: true,
        },
        {
          id: 'i-onion4',
          label: 'Pickled onions',
          removable: true,
        },
        {
          id: 'i-tomato',
          label: 'Beef tomato',
          removable: true,
        },
        {
          id: 'i-harissa',
          label: 'Harissa yoghurt',
          removable: true,
        },
      ],
      extras: [
        {
          id: 'x-halloumi',
          label: 'Extra halloumi',
          price: 12,
          group: 'Add to your burger',
        },
        {
          id: 'x-mushroom',
          label: 'Portobello mushroom',
          price: 10,
          group: 'Add to your burger',
        },
        ...SAUCE_EXTRAS,
        ...SIDE_EXTRAS,
      ],
    },
  },
  {
    id: 'f5',
    title: 'Classic Cheeseburger',
    price: 'AED 28',
    location: 'Corner Diner · Karama',
    images: [BURGER_2, SMASH],
    specs: [
      {
        label: 'Portion',
        value: 'Single patty',
      },
      {
        label: 'Ready in',
        value: '10 min',
      },
      {
        label: 'Spice',
        value: 'None',
      },
      {
        label: 'Rating',
        value: '4.5 · 3.4k',
      },
    ],
    matchScore: 86,
    sellerType: 'Verified Business',
    dish: {
      prepTime: '10 min',
      allergens: ['Gluten', 'Dairy', 'Sesame'],
      calories: '610 kcal',
      sizes: [
        {
          id: 'reg5',
          label: 'Single patty',
          price: 28,
        },
        {
          id: 'dbl5',
          label: 'Double patty',
          price: 38,
        },
      ],
      included: [
        {
          id: 'i-bun5',
          label: 'Sesame bun',
          removable: false,
        },
        {
          id: 'i-beef5',
          label: 'Beef patty',
          removable: false,
        },
        {
          id: 'i-cheese5',
          label: 'Cheddar',
          removable: true,
        },
        {
          id: 'i-onion5',
          label: 'Raw onion',
          removable: true,
        },
        {
          id: 'i-ketchup',
          label: 'Ketchup & mustard',
          removable: true,
        },
      ],
      extras: [
        {
          id: 'x-patty5',
          label: 'Extra patty',
          price: 12,
          group: 'Add to your burger',
        },
        ...SIDE_EXTRAS,
      ],
    },
  },
  {
    id: 'f6',
    title: 'Loaded Truffle Fries',
    price: 'AED 32',
    location: 'Ember House · DIFC',
    images: [TRUFFLE, SMASH],
    specs: [
      {
        label: 'Portion',
        value: 'Sharing',
      },
      {
        label: 'Ready in',
        value: '12 min',
      },
      {
        label: 'Diet',
        value: 'Vegetarian',
      },
      {
        label: 'Rating',
        value: '4.8 · 1.2k',
      },
    ],
    matchScore: 83,
    sellerType: 'Verified Business',
    dish: {
      prepTime: '12 min',
      allergens: ['Dairy'],
      calories: '470 kcal',
      sizes: [
        {
          id: 'solo',
          label: 'Regular',
          price: 24,
        },
        {
          id: 'share',
          label: 'Sharing',
          price: 32,
        },
      ],
      included: [
        {
          id: 'i-fries',
          label: 'Skin-on fries',
          removable: false,
        },
        {
          id: 'i-parm',
          label: 'Parmesan',
          removable: true,
        },
        {
          id: 'i-trufflemayo',
          label: 'Truffle mayo drizzle',
          removable: true,
        },
        {
          id: 'i-chives',
          label: 'Chives',
          removable: true,
        },
      ],
      extras: [
        {
          id: 'x-cheddar',
          label: 'Melted cheddar',
          price: 8,
          group: 'Load it up',
        },
        {
          id: 'x-beef',
          label: 'Pulled beef',
          price: 18,
          group: 'Load it up',
        },
      ],
    },
  },
]

/** Top three for the narrowed result set. */
export const foodNarrowed = foodListings.slice(0, 3)

export const money = (n: number) =>
  `AED ${n.toLocaleString('en-AE', {
    maximumFractionDigits: 0,
  })}`
